import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTherapists } from "../../redux/therapistSlice";
import { getBranches } from "../../redux/branchSlice";
import { getLanguages } from "../../redux/languageSlice";
import DOMPurify from "dompurify";

const toTitleCase = (str) =>
  str ? str.trim().split(/\s+/).map(word =>
    word.charAt(0).toLocaleUpperCase("tr") + word.slice(1).toLowerCase()
  ).join(" ") : "";

const AllExperts = () => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = useSearchParams();
  const itemsPerPage = 48;
  const navigate = useNavigate();
  
  // Redux state ve dispatch'i doğru şekilde kullanma
  const dispatch = useDispatch();
  const therapistsState = useSelector((state) => state.therapist);
  const therapists = therapistsState?.therapists || [];
  const loading = therapistsState?.loading || false;
  const error = therapistsState?.error || null;
  
  // Branches state'i için
  const branchesState = useSelector((state) => state.branches);
  const branches = useMemo(() => branchesState?.branches || [], [branchesState?.branches]);
  const branchesLoading = branchesState?.loading || false;

  const languageState = useSelector((state) => state.languages);
  const fetchedLanguages = languageState?.fetchedLanguages || [];
  
  // URL parametrelerini kontrol et
  useEffect(() => {
    const branchParam = searchParams.get('branch');
    const branchNameParam = searchParams.get('branchName');
    if (branches.length > 0) {
      if (branchParam) {
        const branchExists = branches.some(branch => branch._id === branchParam);
        if (branchExists) {
          setSelectedFilter(branchParam);
          setCurrentPage(1);
        }
      } else if (branchNameParam) {
        const matched = branches.find(
          branch => branch.name.toLowerCase() === branchNameParam.toLowerCase()
        );
        if (matched) {
          setSelectedFilter(matched._id);
          setCurrentPage(1);
        }
      }
    }
  }, [searchParams, branches]);
  
  // Bileşen monte edildiğinde gerekli verileri getir
  useEffect(() => {
    dispatch(getTherapists());
    dispatch(getBranches());
    dispatch(getLanguages());
  }, [dispatch]);

  // Filtreli terapistleri almak için
  const filteredTherapists = selectedFilter === "All"
    ? therapists.filter(
        (therapist) => therapist.isActive !== false && (therapist.approvalStatus === 'approved' || therapist.status === 'approved' || therapist.isApprovedByAdmin === true || therapist.isApprovedByAdmin === 'true' || therapist.isVerified === true || therapist.isVerified === 'true')
      )
    : therapists.filter(
        (therapist) => {
          if (therapist.isActive === false) return false;
          // Onay durumunu kontrol et
          const isApproved = therapist.approvalStatus === 'approved' || therapist.status === 'approved' || therapist.isApprovedByAdmin === true || therapist.isApprovedByAdmin === 'true' || therapist.isVerified === true || therapist.isVerified === 'true';
          if (!isApproved) return false;

          // Hem branchId hem de branchIds array'ini kontrol et
          const hasMainBranch = therapist.branchId?._id === selectedFilter || therapist.branchId === selectedFilter;
          const hasInBranchIds = therapist.branchIds?.includes(selectedFilter);
          return hasMainBranch || hasInBranchIds;
        }
      );
      
  // Terapistlerin toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(filteredTherapists.length / itemsPerPage);
  
  // Mevcut sayfadaki terapistleri al
  const currentTherapists = filteredTherapists.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Bileşen monte edildiğinde terapistleri ve branşları getir
  useEffect(() => {
    dispatch(getTherapists());
    dispatch(getBranches());
  }, [dispatch]);
  
  // Filtre değiştirme işleyicisi
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setCurrentPage(1);
    
    // URL'yi güncelle (opsiyonel - kullanıcı deneyimini iyileştirir)
    if (filter === "All") {
      // Eğer "All" seçilirse URL parametrelerini temizle
      navigate('/experts', { replace: true });
    } else {
      // Seçilen branch için URL parametrelerini güncelle
      const selectedBranch = branches.find(branch => branch._id === filter);
      if (selectedBranch) {
        navigate(`/experts?branch=${filter}&name=${encodeURIComponent(selectedBranch.name)}`, { replace: true });
      }
    }
  };
  
  // Sayfa değiştirme işleyicisi
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  // HTML içeriğini güvenli bir şekilde render etmek için ve kısaltma
  const createMarkup = (html, limit = 150) => {
    if (!html) return { __html: "" };
    
    // HTML etiketlerini temizle ve düz metne dönüştür
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    
    // Metni kısalt
    if (textContent.length > limit) {
      return { __html: DOMPurify.sanitize(textContent.substring(0, limit) + "...") };
    }
    
    return { __html: DOMPurify.sanitize(html) };
  };

  // Eğitim bilgisini formatlama
  const formatEducation = (education) => {
    if (!education || education.length === 0) return "Eğitim bilgisi yok";
    return education[0].school;
  };
  
  // Yorum sayısını ve puanını formatlama
  const formatReviews = (reviews) => {
    if (!reviews || reviews.length === 0) return { count: 0, rating: 5 };
    
    // Gerçek yorum sayısı
    const count = reviews.length;
    
    // Ortalama puanı hesapla (varsayılan olarak 5 üzerinden)
    // Gerçek uygulamada reviews içinde rating değeri olacaktır
    const rating = 5;
    
    return { count, rating };
  };
  
  // Filtre listesi - Database'den gelen branşları kullan
  const filters = [
    { id: "All", name: t("All") },
    ...branches.map(branch => ({ id: branch._id, name: branch.name }))
  ];

  // Yükleme durumunu kontrol et
  if (loading || branchesLoading) {
    return <div className="text-center mt-10">Yükleniyor...</div>;
  }

  // Hata durumunu kontrol et
  if (error) {
    return <div className="text-center mt-10 text-red-500">Hata: {error}</div>;
  }

  return (
    <div className="p-6 min-h-screen mt-[46px]">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-[36px] font-poppins text-text">
          {t("therHappysExpertPsychologistStaff")}
        </h1>
        <div className="flex justify-center mt-4 flex-wrap">
          {/* Filtre Butonları */}
          <div className="flex flex-wrap justify-center gap-2">
            {filters.map((filter) => (
              <button
                key={`filter-${filter.id}`}
                className={`px-4 py-2 rounded-full font-roboto ${
                  selectedFilter === filter.id
                    ? "bg-button-bg text-white"
                    : "bg-background text-text"
                }`}
                onClick={() => handleFilterChange(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Terapist kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-y-10 gap-x-6 px-4 sm:px-6 lg:px-[125px] mt-[67px]">
        {currentTherapists.length > 0 ? (
          currentTherapists.map((therapist) => {
            // Yorum bilgilerini al
            const { count: rating } = formatReviews(therapist.reviews);
            
            return (
              <div
                key={`therapist-${therapist._id}`}
                className="relative w-full max-w-[370px] min-h-[406px] mx-auto overflow-hidden border-2 rounded-[45px] shadow-md px-4 py-6 bg-background border-cards-border flex flex-col justify-between"
              >
                {/* Üst Alan */}
                <div className="flex items-center flex-wrap">
                  <div className="w-[99px] h-[99px]">
                    {therapist.picture ? (
                      <img 
                        src={therapist.picture} 
                        alt={`${therapist.name} ${therapist.surname}`}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xl">
                        {therapist.name.charAt(0)}{therapist.surname.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="ml-4 text-center">
                    <h3 className="text-[20px] font-poppins italic text-text">
                      {toTitleCase(therapist.name)} {therapist.surname}
                    </h3>
                    <p className="text-[16px] text-gray-600 font-poppins italic ml-2">
                      {therapist.branchId?.name || "Therapist"}
                    </p>
                    <hr className="my-2 border-gray-300" />
                    <div className="flex items-center justify-center gap-1">
                      {/* Yıldızlar - Gerçek rating'e göre dolu veya boş yıldız göster */}
                      {Array(5)
                        .fill(0)
                        .map((_, i) => (
                          <svg
                            key={`star-${therapist._id}-${i}`}
                            xmlns="http://www.w3.org/2000/svg"
                            fill={i < rating ? "currentColor" : "none"}
                            stroke={i < rating ? "none" : "currentColor"}
                            viewBox="0 0 24 24"
                            className="w-4 h-4 text-yellow-400"
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                          </svg>
                        ))}
                    </div>
                  </div>
                </div>

                {/* Orta Alan - Bio veya Eğitim Bilgisi - Scroll yerine kısaltılmış metin */}
                <div className="mt-8 w-full max-w-[330px] h-[110px] mx-auto overflow-hidden">
                  {therapist.bio ? (
                    <div
                      className="font-poppins italic text-[16px] text-left break-words"
                      dangerouslySetInnerHTML={createMarkup(therapist.bio, 150)}
                    />
                  ) : (
                    <p className="font-poppins italic text-[16px] text-left break-words">
                      {formatEducation(therapist.education)}
                    </p>
                  )}
                </div>

                {/* Alt Alan - Sertifikalar ve Buton */}
                <div className="flex justify-between items-center flex-wrap mt-8 gap-y-3">
                  <div className="flex flex-col">
                    <span className="text-[12px] font-poppins font-bold text-primary px-1 uppercase mb-1">
                      {t("languages")}
                    </span>
                    <hr className="border-text border-[2px] rounded mb-1" />
                    {therapist.languages && therapist.languages.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {[...therapist.languages]
                          .sort((a, b) => {
                            const getCodeLabel = (lang) => {
                              const langId = typeof lang === 'object' ? lang._id : lang;
                              const found = fetchedLanguages.find(l => l._id === langId);
                              const code = (found?.code || (typeof lang === 'object' ? lang.code : '') || '').toUpperCase();
                              const name = (found?.name || (typeof lang === 'object' ? lang.name : '') || '').toUpperCase();
                              return { code, name };
                            };
                            const labelA = getCodeLabel(a);
                            const labelB = getCodeLabel(b);
                            const isEnA = labelA.code === 'EN' || labelA.name === 'ENGLISH';
                            const isEnB = labelB.code === 'EN' || labelB.name === 'ENGLISH';
                            if (isEnA && !isEnB) return -1;
                            if (!isEnA && isEnB) return 1;
                            return 0;
                          })
                          .map((langData, idx) => {
                            const langId = typeof langData === 'object' ? langData._id : langData;
                            const foundLang = fetchedLanguages.find(l => l._id === langId);
                            const displayLabel = foundLang 
                              ? (foundLang.code || foundLang.name) 
                              : (typeof langData === 'object' ? (langData.code || langData.name) : '...');
                            
                            return (
                              <span 
                                key={idx} 
                                className="text-[10px] font-poppins font-bold text-primary bg-tertiary px-1 rounded uppercase"
                              >
                                {displayLabel}
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/therapistInfo/${therapist._id}`)}
                    className="py-2 px-4 w-[150px] h-[43px] rounded-full font-roboto text-[16px] text-white transition-transform transform hover:scale-105 active:scale-95 duration-300 ease-in-out bg-button-bg"
                  >
                    {t("detailTherapist")}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-3 text-center mt-10">
            <div className="text-lg text-gray-600">
              {selectedFilter === "All" 
                ? "Henüz terapist bulunamadı" 
                : `${branches.find(b => b._id === selectedFilter)?.name || "Bu branş"} için terapist bulunamadı`
              }
            </div>
          </div>
        )}
      </div>

      {/* Sayfalama */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-10 gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-full ${
              currentPage === 1
                ? "bg-gray-300 text-gray-500"
                : "bg-button-bg text-white hover:bg-tertiary hover:text-primary"
            }`}
          >
            &lt;
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={`page-${page}`}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded-full ${
                currentPage === page
                  ? "bg-button-bg text-white"
                  : "bg-background text-text hover:bg-tertiary hover:text-primary"
              }`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-full ${
              currentPage === totalPages
                ? "bg-gray-300 text-gray-500"
                : "bg-button-bg text-white hover:bg-tertiary hover:text-primary"
            }`}
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
};

export default AllExperts;
